import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MobileNavbarComponent } from '../mobile-navbar/mobile-navbar.component';
import { DetailedTag } from 'src/services/tag/DetailedTag';
import { isRoot } from 'src/utils/isRoot';
import { CommonModule } from '@angular/common';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { EditTagBottomSheet } from '../edit-tag-bottom-sheet/edit-tag-bottom-sheet.component';
import { getTopLevelTag } from 'src/utils/getTopLevelTag';
import { CreateTagBottomSheetComponent } from '../create-tag-bottom-sheet/create-tag-bottom-sheet.component';
import { move } from 'src/utils/move';
import { TagService } from 'src/services/tag/tag.service';
import { UserService } from 'src/services/user/user.service';
import { getTagColor } from 'src/utils/getTagColor';
import { getTagSummary } from 'src/utils/getTagSummary';
import { trackByTagId } from 'src/utils/trackByTagId';
import { archiveSelected } from 'src/utils/archiveSelected';
import { AuthService } from 'src/services/auth/auth.service';
import { UpsellBottomSheetComponent } from 'src/app/components/upsell-bottom-sheet/upsell-bottom-sheet.component';
import { WelcomeToThePlannerComponent } from 'src/app/components/welcome-to-the-planner/welcome-to-the-planner.component';

@Component({
  selector: 'mobile-planner',
  standalone: true,
  imports: [
    CommonModule,
    MobileNavbarComponent,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    WelcomeToThePlannerComponent,
  ],
  templateUrl: './mobile-planner.component.html',
  styleUrl: './mobile-planner.component.scss',
})
export class MobilePlannerComponent {
  @Output()
  onHamburgerClick = new EventEmitter<void>();

  @Output()
  onOpenSettingsForPrefundPro = new EventEmitter<void>();

  @Input()
  detailedTags: DetailedTag[] = [];

  @Input()
  detailedArchivedTags: DetailedTag[] = [];

  private _bottomSheet = inject(MatBottomSheet);

  getTagColor = getTagColor;

  constructor(
    private userService: UserService,
    private tagService: TagService,
    private authService: AuthService
  ) {}

  isRoot = isRoot;
  getTopLevelTag = getTopLevelTag;
  getTagSummary = getTagSummary;
  trackByTagId = trackByTagId;

  openEditTagBottomSheet(tag: DetailedTag) {
    this._bottomSheet
      .open(EditTagBottomSheet, {
        data: {
          tag: { ...tag },
          tags: [...this.detailedTags],
          archivedTags: [...this.detailedArchivedTags],
        },
      })
      .afterDismissed()
      .subscribe((shouldOpenSettingsForPrefundPro) => {
        if (shouldOpenSettingsForPrefundPro) {
          this.onOpenSettingsForPrefundPro.emit();
        }
      });
  }

  getColor(tag: DetailedTag) {
    return getTagColor(tag, [
      ...this.detailedTags,
      ...this.detailedArchivedTags,
    ]);
  }

  openCreateTagBottomSheetOrUpsell() {
    this.openCreateTagBottomSheet();
  }

  private openUpsellBottomSheet() {
    this._bottomSheet
      .open(UpsellBottomSheetComponent)
      .afterDismissed()
      .subscribe((shouldOpenSettingsForPrefundPro) => {
        if (shouldOpenSettingsForPrefundPro) {
          this.onOpenSettingsForPrefundPro.emit();
        }
      });
  }

  openCreateTagBottomSheet() {
    // Rediredt to Settings/Upsell with a custom message
    this._bottomSheet
      .open(CreateTagBottomSheetComponent, {
        data: {
          tagsLength: [...this.detailedTags, ...this.detailedArchivedTags]
            .length,
        },
      })
      .afterDismissed()
      .subscribe(
        (data: {
          newFullPath: string;
          shouldOpenSettingsForPrefundPro: boolean;
        }) => {
          if (data.shouldOpenSettingsForPrefundPro) {
            this.onOpenSettingsForPrefundPro.emit();
          } else {
            const newTag = this.detailedTags.find(
              (tag) => tag.fullPath === data.newFullPath
            );
            if (newTag) {
              this.openEditTagBottomSheet(newTag);
            }
          }
        }
      );
  }

  move(tag: DetailedTag, direction: 'up' | 'down') {
    this.detailedTags = [...move(this.detailedTags, tag, direction)];
    const ids: string[] = this.detailedTags
      .map((t) => t.id)
      .filter((id) => id !== undefined) as string[];
    this.userService.savePreferredTagOrder(ids);
  }

  hasArchivedTags() {
    return (
      this.detailedArchivedTags !== undefined &&
      this.detailedArchivedTags?.length > 0
    );
  }

  getFixedTags() {
    return this.detailedTags.filter(
      (t) => !getTopLevelTag(t, this.detailedTags)?.isPeriodic
    );
  }

  getPeriodicTags() {
    return this.detailedTags.filter(
      (t) => getTopLevelTag(t, this.detailedTags)?.isPeriodic
    );
  }
}
